import React, { useEffect, useMemo, useRef, useState } from 'react';
import fnc from '../../mymod/commFunction';
import './QuebankQuestionCount.scss';

function formatCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? count.toLocaleString('ko-KR') : '0';
}

function formatProblemDate(value) {
    const problem = String(value || '').trim();
    const matched = problem.match(/(?:^|\D)((?:19|20)\d{2})(\d{2})(\d{2})/);
    if (!matched) return problem;

    const [, year, month, day] = matched;
    const parsedDate = new Date(`${year}-${month}-${day}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return problem;

    return `${year}-${month}-${day}`;
}

const QuebankQuestionCount = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [keyword, setKeyword] = useState('');
    const [refreshToken, setRefreshToken] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: 'question_cnt', direction: 'desc' });
    const [selectedUserId, setSelectedUserId] = useState('');
    const requestIdRef = useRef(0);

    useEffect(() => {
        const requestId = requestIdRef.current + 1;
        let responseReceived = false;
        requestIdRef.current = requestId;
        setLoading(true);
        setErrorMessage('');

        const request = fnc.sendAxios('/userRoutes/selectQuebankQuestionCounts', {}, (res) => {
            if (requestIdRef.current !== requestId) return;
            responseReceived = true;

            if (!Array.isArray(res)) {
                setLoading(false);
                setErrorMessage('QUEBANK 결과를 불러오지 못했습니다.');
                return;
            }

            setRows(res.map((item) => ({
                user_id: String(item?.user_id || '').trim(),
                user_name: String(item?.user_name || '').trim(),
                question_cnt: Number(item?.question_cnt ?? item?.cnt) || 0,
                solution_cnt: Number(item?.solution_cnt) || 0,
                answer_cnt: Number(item?.answer_cnt) || 0,
                last_problem: String(item?.last_problem || '').trim()
            })));
            setLoading(false);
        });

        Promise.resolve(request).then(() => {
            if (requestIdRef.current !== requestId || responseReceived) return;
            setLoading(false);
            setErrorMessage('QUEBANK 조회에 실패했습니다. DB 설정을 확인해 주세요.');
        });

        const timeoutId = window.setTimeout(() => {
            if (requestIdRef.current !== requestId || responseReceived) return;
            setLoading(false);
            setErrorMessage('QUEBANK 조회 시간이 초과되었습니다.');
        }, 30000);

        return () => {
            window.clearTimeout(timeoutId);
            requestIdRef.current += 1;
        };
    }, [refreshToken]);

    const visibleRows = useMemo(() => {
        const normalizedKeyword = keyword.trim().toLowerCase();
        const filteredRows = normalizedKeyword
            ? rows.filter((item) => (
                item.user_id.toLowerCase().includes(normalizedKeyword)
                || item.user_name.toLowerCase().includes(normalizedKeyword)
            ))
            : rows;

        return [...filteredRows].sort((a, b) => {
            const direction = sortConfig.direction === 'asc' ? 1 : -1;
            if (['question_cnt', 'solution_cnt', 'answer_cnt'].includes(sortConfig.key)) {
                return (a[sortConfig.key] - b[sortConfig.key]) * direction;
            }
            return String(a[sortConfig.key] || '').localeCompare(String(b[sortConfig.key] || ''), 'ko') * direction;
        });
    }, [rows, keyword, sortConfig]);

    const summaryRows = useMemo(
        () => rows.filter((item) => item.user_name !== '홍대화'),
        [rows]
    );
    const totalCounts = useMemo(
        () => summaryRows.reduce((totals, item) => ({
            question: totals.question + item.question_cnt,
            solution: totals.solution + item.solution_cnt,
            answer: totals.answer + item.answer_cnt
        }), { question: 0, solution: 0, answer: 0 }),
        [summaryRows]
    );

    function handleSort(key) {
        setSortConfig((previousConfig) => ({
            key,
            direction: previousConfig.key === key && previousConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    }

    return (
        <div className="quebank-grid-view">
            <div className="quebank-grid-toolbar">
                <div className="quebank-grid-summary">
                    <span>전체 사용자 <strong>{formatCount(summaryRows.length)}</strong>명</span>
                    <span>문 <strong>{formatCount(totalCounts.question)}</strong>건</span>
                    <span>풀 <strong>{formatCount(totalCounts.solution)}</strong>건</span>
                    <span>답 <strong>{formatCount(totalCounts.answer)}</strong>건</span>
                    <button
                        type="button"
                        className="quebank-refresh"
                        onClick={() => setRefreshToken((value) => value + 1)}
                        disabled={loading}
                    >
                        {loading ? '조회 중...' : '새로고침'}
                    </button>
                    {keyword.trim() && <span>검색 결과 <strong>{formatCount(visibleRows.length)}</strong>명</span>}
                </div>

                <div className="quebank-grid-actions">
                    <input
                        type="text"
                        value={keyword}
                        onChange={(event) => setKeyword(event.target.value)}
                        placeholder="user_id / 이름 검색"
                    />
                    <button type="button" onClick={() => setKeyword('')} disabled={!keyword}>
                        초기화
                    </button>
                </div>
            </div>

            <div className="quebank-grid-wrap">
                <table className="quebank-grid-table">
                    <thead>
                        <tr>
                            <th>no</th>
                            <th onClick={() => handleSort('user_id')}>user_id</th>
                            <th onClick={() => handleSort('user_name')}>이름</th>
                            <th onClick={() => handleSort('question_cnt')}>문</th>
                            <th onClick={() => handleSort('solution_cnt')}>풀</th>
                            <th onClick={() => handleSort('answer_cnt')}>답</th>
                            <th onClick={() => handleSort('last_problem')}>마지막 날짜</th>
                            <th aria-label="빈 공간"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8" className="quebank-grid-message">QUEBANK 조회 중...</td></tr>
                        ) : errorMessage ? (
                            <tr><td colSpan="8" className="quebank-grid-message error">{errorMessage}</td></tr>
                        ) : visibleRows.length === 0 ? (
                            <tr><td colSpan="8" className="quebank-grid-message">조회 결과가 없습니다.</td></tr>
                        ) : (
                            visibleRows.map((item, index) => (
                                <tr
                                    key={item.user_id}
                                    className={selectedUserId === item.user_id ? 'selected' : ''}
                                    onClick={() => setSelectedUserId(item.user_id)}
                                >
                                    <td>{index + 1}</td>
                                    <td>{item.user_id}</td>
                                    <td className={item.user_name !== '홍대화' ? 'quebank-other-user-name' : ''}>
                                        {item.user_name}
                                    </td>
                                    <td>{formatCount(item.question_cnt)}</td>
                                    <td>{formatCount(item.solution_cnt)}</td>
                                    <td>{formatCount(item.answer_cnt)}</td>
                                    <td title={item.last_problem}>{formatProblemDate(item.last_problem)}</td>
                                    <td></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default QuebankQuestionCount;
